import React from 'react'
import { getStatusColor } from 'constants/status'
import { request } from 'utilities/request'
import { showDate } from 'utilities/form'
import { textFilter, dateFilter, selectFilter } from 'utilities/filter'
import { getLimit, getPagination } from 'utilities/pagination'
import { LinkButton, StatusChip, Link } from 'components/core'
import { MdEdit } from 'react-icons/md'
import { renderDeleteButton } from 'utilities/app'
import { handleDelete } from 'actions/ticket'
import { FILTER_SET } from 'constants/actionType'

export const filters = (locations) => [
  {
    id: 'id',
    label: 'field.ticketId',
    input: textFilter,
  },
  {
    id: 'transDate',
    label: 'field.date',
    input: dateFilter,
    // defaultValue: [getDate(), addDays(getDate(), 1)],
  },
  {
    id: 'partName',
    label: 'field.partName',
    input: textFilter,
  },
  {
    id: 'sku',
    label: 'field.sku',
    input: textFilter,
  },
  {
    id: 'fromLocationId',
    label: 'move.field.fromTech',
    input: (props) =>
      selectFilter({
        ...props,
        isSearchable: true,
        options: getLocationOptions(locations, props.message),
      }),
  },
  {
    id: 'toLocationId',
    label: 'move.field.toTech',
    input: (props) =>
      selectFilter({
        ...props,
        isSearchable: true,
        options: getLocationOptions(locations, props.message),
      }),
  },
]

export function getLocationOptions(locations, message) {
  if (!locations) return []
  const warehouses = []
  const techs = []
  locations.forEach(({ type, id, name }) => {
    const result = { label: name, value: id }

    switch (type) {
      case 'PART_WAREHOUSE':
        warehouses.push(result)
        break
      case 'TECHNICIAN':
        techs.push(result)
        break
      default:
    }
  })
  return [
    { label: message({ id: 'location.type.WAREHOUSE' }), options: warehouses },
    { label: message({ id: 'location.type.TECHNICIAN' }), options: techs },
  ]
}

export const columns = ({ session, app, message, action }) => [
  {
    id: 'id',
    label: 'field.ticketId',
    format: ['html', 'excel'],
    renderHtml: ({ row }) => (
      <Link variant="primaryLink" href={`/move/${row.id}/view`}>
        {row.id}
      </Link>
    ),
  },
  {
    id: 'transDate',
    label: 'field.date',
    format: ['html', 'excel'],
    render: ({ row }) => showDate(row.transDate),
  },
  {
    id: 'fromLocationName',
    label: 'move.field.fromTech',
    format: ['html', 'excel'],
  },
  {
    id: 'toLocationName',
    label: 'move.field.toTech',
    format: ['html', 'excel'],
  },
  {
    id: 'productVariantName',
    label: 'field.partName',
    format: ['excel'],
  },
  {
    id: 'sku',
    label: 'field.sku',
    format: ['excel'],
  },
  {
    id: 'quantity',
    label: 'field.quantity',
    format: ['excel'],
  },
  {
    id: 'status',
    label: 'field.status',
    format: ['html', 'excel'],
    renderHtml: ({ row }) => (
      <StatusChip
        label={`status.${row.status}`}
        color={getStatusColor(row.status)}
      />
    ),
    render: ({ row }) => message({ id: `status.${row.status}` }),
  },
  {
    id: 'memo',
    label: 'field.memo',
    format: ['excel'],
  },
  {
    id: 'actions',
    align: 'right',
    renderHtml: ({ row }) => (
      <>
        <LinkButton
          mr={2}
          disabled={row.status === 'INACTIVE'}
          variant="icon"
          icon={<MdEdit />}
          href={`/move/${row.id}/edit`}
        />
        {renderDeleteButton({
          module: 'move',
          session,
          status: row.status,
          onSubmit: () =>
            handleDelete('partMove', {
              session,
              app,
              id: row.id,
              hash: row.hash,
              action,
            }),
        })}
      </>
    ),
  },
]

export const handlers = ({
  session,
  app,
  message,
  setState,
  setFilterValues,
  setPagination,
  setLocations,
}) => ({
  handleLoad: async ({ pagination, filterValues } = {}) => {
    const filterId = 'moveList'
    if (!filterValues) {
      filterValues = session.state.filterMap[filterId] || []
      pagination = session.state.paginationMap[filterId] || []
    }
    const resp = await getData({ session, app, pagination, filterValues })
    setFilterValues(filterValues)
    setState(resp.data)
    setPagination(resp.pagination)
    setLocations(resp.locations)
    session.dispatch({
      type: FILTER_SET,
      key: filterId,
      filterValues,
      pagination,
    })
  },
  handlerExport: async ({ filterValues, createExcel }) => {
    const title = 'move.title.list'
    const cols = columns({ message }).filter(({ format = [] }) =>
      format.includes('excel'),
    )
    const rows = await getExportData({ session, app, filterValues })
    createExcel({ message, title, cols, rows })
  },
})

async function getData({ session, app, pagination = {}, filterValues = [] }) {
  const variables = {
    locationInput: { type: ['PART_WAREHOUSE', 'TECHNICIAN'] },
    input: getDataInput({ pagination, filterValues }),
  }
  const query = `
    query($input: TicketQueryInput, $locationInput: LocationQueryInput) {
      locations(input: $locationInput) {
        id
        name
        type
      }
      partMoveTicketCount(input: $input)
      partMoveTickets(input: $input) {
        id
        ticketNo
        fromLocationName
        toLocationName
        transDate
        status
        hash
      }
    }
  `
  const [ok, data] = await request({ query, variables }, { session, app })
  if (!ok) return {}

  // const locations = filterLocations(app.state.staff, data.locations)
  const { page, countPerPage } = pagination
  return {
    data: data.partMoveTickets,
    pagination: getPagination(page, countPerPage, data.partMoveTicketCount),
    locations: data.locations,
  }
}

async function getExportData({ session, app, filterValues }) {
  const variables = { input: getDataInput({ filterValues, joinItem: true }) }
  const query = `
    query($input: TicketQueryInput) {
      partMoveTickets(input: $input) {
        id
        ticketNo
        fromLocationName
        toLocationName
        productVariantName
        sku
        quantity
        memo
        extra
        transDate
        status
      }
    }
  `
  const [ok, data] = await request({ query, variables }, { session, app })
  if (!ok) return []

  return data.partMoveTickets.map((item) => {
    const { extra } = item
    item.billingName = extra.billCompany?.name
    item.memo = extra.memo
    return item
  })
}

function getDataInput({ filterValues, pagination, joinItem = false }) {
  const id = filterValues.find(({ id }) => id === 'id')
  const transDate = filterValues.find(({ id }) => id === 'transDate')
  const partName = filterValues.find(({ id }) => id === 'partName')
  const sku = filterValues.find(({ id }) => id === 'sku')
  const fromLocationId = filterValues.find(({ id }) => id === 'fromLocationId')
  const toLocationId = filterValues.find(({ id }) => id === 'toLocationId')
  const input = {
    joinItem,
    id: id?.value,
    transDate: transDate?.value,
    fromLocationId: fromLocationId?.value.value,
    toLocationId: toLocationId?.value.value,
    productVariantName: partName?.value,
    sku: sku?.value,
    orderBy: [{ key: 'id', sort: 'DESC' }],
  }
  if (pagination) input.limit = getLimit(pagination)
  return input
}
